import React, { useEffect, useMemo, useState } from "react";
import { ResponsiveDataTable } from "../v2/components/ui/index.ts";

/** Groups timesheet entries by project; OTHERS → Miscellaneous (matches WFH grouping). */
function groupTimesheetEntries(entries) {
  if (!entries?.length) return [];
  const map = new Map();

  for (const entry of entries) {
    if (entry.titleType === "OTHERS") {
      const key = "others";
      if (!map.has(key)) {
        map.set(key, { key, label: "Miscellaneous", entries: [] });
      }
      map.get(key).entries.push(entry);
      continue;
    }

    const key =
      entry.projectId != null
        ? `project-${entry.projectId}`
        : `project-${String(entry.projectName || "unnamed").replace(/\s+/g, "-")}`;
    if (!map.has(key)) {
      map.set(key, {
        key,
        label: entry.projectName || "Project",
        entries: [],
      });
    }
    map.get(key).entries.push(entry);
  }

  return Array.from(map.values());
}

function isApiTimesheetEntries(timesheet) {
  if (!Array.isArray(timesheet) || timesheet.length === 0) return false;
  const x = timesheet[0];
  if (x == null || typeof x !== "object") return false;
  if (x.duration != null && x.duration !== "") return true;
  if (x.titleType != null) return true;
  if (x.titleName != null && x.taskName == null) return true;
  return false;
}

function formatDurationParts(totalMinutes) {
  const m = Math.max(0, Number(totalMinutes) || 0);
  return { h: Math.floor(m / 60), mm: m % 60 };
}

/** Slug-style API values, e.g. `internal-discussions` → `Internal Discussions`. */
function formatCategoryLabel(raw) {
  if (raw == null || String(raw).trim() === "") return "—";
  const t = String(raw).trim();
  if (t === "—") return "—";
  const spaced = t.replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
  if (!spaced) return "—";
  return spaced.replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function taskStatusLabel(raw) {
  const s = String(raw ?? "")
    .toUpperCase()
    .replace(/-/g, "_");
  if (s === "COMPLETED") return "Completed";
  if (s === "IN_PROGRESS" || s === "INPROGRESS") return "In-progress";
  if (!raw) return "—";
  return String(raw);
}

/** Plain text, e.g. `3h : 2m` */
function formatTimeHm(h, mm) {
  const hours = Number(h) || 0;
  const mins = Number(mm) || 0;
  return `${hours}h : ${mins}m`;
}

const footerBg = "#f0f7ff";

const ViewOODTimesheet = ({
  timesheet,
  showTotalTime,
  setTotalTime: _setTotalTime,
}) => {
  const [totalTime, setTotalTime] = useState({
    hours: "",
    minutes: "",
  });

  const { tableData, totalMinutes, entryCount } = useMemo(() => {
    const list = timesheet || [];
    if (!list.length) {
      return { tableData: [], totalMinutes: 0, entryCount: 0 };
    }

    if (isApiTimesheetEntries(list)) {
      const groups = groupTimesheetEntries(list);
      let rowId = 0;
      const rows = [];
      let sumMin = 0;

      for (const group of groups) {
        for (let idx = 0; idx < group.entries.length; idx++) {
          const entry = group.entries[idx];
          const dur = Number(entry.duration) || 0;
          sumMin += dur;
          const { h, mm } = formatDurationParts(dur);
          rows.push({
            id: `ood-${rowId++}`,
            project: idx === 0 ? group.label : "",
            category: formatCategoryLabel(entry.titleName),
            description: entry.description ?? "—",
            timeLabel: formatTimeHm(h, mm),
            taskStatusLabel: taskStatusLabel(entry.taskStatus),
          });
        }
      }

      return {
        tableData: rows,
        totalMinutes: sumMin,
        entryCount: list.length,
      };
    }

    let sumMin = 0;
    const legacyEntries = list.map((task) => {
      const h = Number(task.hours) || 0;
      const mi = Number(task.minutes) || 0;
      sumMin += h * 60 + mi;
      return {
        titleName: "—",
        description: task.taskName ?? "—",
        duration: h * 60 + mi,
        taskStatus: null,
      };
    });

    const groups = [{ key: "legacy", label: "—", entries: legacyEntries }];
    let rowId = 0;
    const rows = [];
    for (const group of groups) {
      for (let idx = 0; idx < group.entries.length; idx++) {
        const entry = group.entries[idx];
        const dur = Number(entry.duration) || 0;
        const { h, mm } = formatDurationParts(dur);
        rows.push({
          id: `ood-${rowId++}`,
          project: idx === 0 ? group.label : "",
          category: formatCategoryLabel(entry.titleName),
          description: entry.description ?? "—",
          timeLabel: formatTimeHm(h, mm),
          taskStatusLabel: taskStatusLabel(entry.taskStatus),
        });
      }
    }

    return {
      tableData: rows,
      totalMinutes: sumMin,
      entryCount: list.length,
    };
  }, [timesheet]);

  useEffect(() => {
    const m = Math.max(0, Number(totalMinutes) || 0);
    const finalTotalTime = {
      hours: Math.floor(m / 60),
      minutes: m % 60,
    };
    setTotalTime(finalTotalTime);
    _setTotalTime?.(finalTotalTime);
  }, [totalMinutes, _setTotalTime]);

  const columns = useMemo(
    () => [
      {
        id: "project",
        header: "Project",
        accessor: "project",
        sortable: false,
        truncate: true,
        getTitleText: (row) => String(row.project ?? ""),
      },
      {
        id: "category",
        header: "Category",
        accessor: "category",
        sortable: false,
        truncate: true,
        getTitleText: (row) => String(row.category ?? ""),
      },
      {
        id: "description",
        header: "Task Description",
        accessor: "description",
        sortable: false,
        truncate: true,
        getTitleText: (row) => String(row.description ?? ""),
      },
      {
        id: "time",
        header: "Time",
        accessor: "timeLabel",
        sortable: false,
        nowrap: true,
        getTitleText: (row) => String(row.timeLabel ?? ""),
      },
      {
        id: "taskStatus",
        header: "Task Status",
        accessor: "taskStatusLabel",
        sortable: false,
        nowrap: true,
        getTitleText: (row) => String(row.taskStatusLabel ?? ""),
      },
    ],
    [],
  );


  const mobileSummary = useMemo(() => {
    if (!tableData.length) return null;
    const { h, mm } = formatDurationParts(totalMinutes);
    return (
      <div
        className="body-sm"
        style={{
          padding: "12px 16px",
          background: footerBg,
          boxShadow: "inset 0 4px 0 0 #cce3ff",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontWeight: 600 }}>Daily Total</span>
          <span style={{ whiteSpace: "nowrap" }}>{formatTimeHm(h, mm)}</span>
        </div>
        <p
          style={{
            margin: "8px 0 0",
            color: "var(--color-text-secondary, #64748b)",
          }}
        >
          {entryCount} {entryCount === 1 ? "entry" : "entries"}
        </p>
      </div>
    );
  }, [tableData.length, totalMinutes, entryCount]);

  return (
    <div className="d-flex flex-column gap-4">
      {showTotalTime ? (
        <div className="timesheet-total-time d-flex gap-2 align-items-center justify-content-center">
          <span>Total Time</span>
          <div className="d-flex">
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ gap: "2px" }}
            >
              <span>{totalTime.hours}</span>
              <span>Hrs</span>
            </div>
            {totalTime.minutes > 0 && (
              <>
                <span className="d-flex align-items-center justify-content-center px-2">
                  :
                </span>
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{ gap: "2px" }}
                >
                  <span>{totalTime.minutes}</span>
                  <span>Mins</span>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      <ResponsiveDataTable
        pagination={false}
        columns={columns}
        data={tableData}
        aria-label="Timesheet tasks"

        mobileSummary={mobileSummary}
        emptyState={
          <span className="body-sm" style={{ color: "var(--content-muted)" }}>
            No tasks
          </span>
        }
      />
    </div>
  );
};

export default ViewOODTimesheet;
